import { Component, OnInit, SecurityContext } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

interface KnownIssue {
    id: string
    date: string
    title: string
    entityType: 'subscription' | 'payment' | 'merchant' | 'client' | 'other'
    entityId: string
    status: 'investigating' | 'resolved' | 'monitoring' | 'wont-fix'
    severity: 'critical' | 'high' | 'medium' | 'low'
    description: string
    auditFile: string
    auditContent: string
    resolution?: string
    rootCause?: string
    expanded?: boolean
}

@Component({
    selector: 'app-admin-known-issues',
    templateUrl: './admin-known-issues.component.html',
    styleUrls: ['./admin-known-issues.component.scss'],
})
export class AdminKnownIssuesComponent implements OnInit {
    constructor(private sanitizer: DomSanitizer) {}

    knownIssues: KnownIssue[] = [
        {
            id: '2026-05-07-001',
            date: '2026-05-07',
            title: 'Triple Invoice / Month-End Date Drift Bug',
            entityType: 'subscription',
            entityId: 'marcel-invoice-8045',
            status: 'investigating',
            severity: 'critical',
            description:
                'A 2-month subscription starting on 30 Jan generated three invoices (Jan 30, Feb 28, March 28) instead of two. moment().add(1, "M") clamps Jan 30 → Feb 28, then Feb 28 → March 28, which lands one day before the endDate (March 29) so a third invoice is generated. Affects every fixed-term sub starting on the 29th/30th/31st whose renewals cross a shorter month.',
            auditFile: 'readmes/audits/2026-05-07_TRIPLE_INVOICE_MONTH_END_DRIFT_marcel.md',
            rootCause: 'moment.js add(1, "M") clamps to the last day of a shorter target month and forgets the original day-of-month. Combined with the gating condition "nextDebitDate < endDate" in setNextDebitDate() (subscription.service.ts:211) and updateNextDebitDate() (paygate.fuctions.ts:796), this causes the renewal anchor to drift earlier each cycle and slip in one extra invoice before terminating. There is no paymentsMade < period guard.',
            auditContent: `# Subscription Audit: Triple Invoice / Month-End Date Drift Bug

**Audit Date:** 2026-05-07
**Affected Client:** Marcel
**Disputed Invoice:** #8045 (third invoice on a 2-month subscription)
**Other Invoices on Subscription:** #7638 (Jan 30), #7787 (Feb 28)
**Status:** ROOT CAUSE IDENTIFIED — code fix and refund pending
**Severity:** Critical (real money double-charged; affects every fixed-term sub starting on the 29th/30th/31st)

---

## Issue Summary

Marcel's subscription is a **2-month fixed-term** parking subscription that started on **30 January**. She was expected to be charged twice (Jan 30 + Feb 28). She was charged **three times** (Jan 30, Feb 28, **March 28**), against this single subscription.

| Invoice | Generated | Paid | Expected? |
|---------|-----------|------|-----------|
| 7638 | Jan 30 | Jan 30 (R800) | ✓ |
| 7787 | ~Feb 21 | Feb 28 (R800) | ✓ |
| **8045** | **March 21** | **March 28 (R800)** | **✗ DUPLICATE** |

The subscription document itself reports period = 2 months but paymentsMade = 3, with nextDebitDate = null after the third payment (so the bug self-terminates rather than running forever).

---

## ROOT CAUSE IDENTIFIED

### One-line summary

moment().add(1, "M") collapses Jan 30 → Feb 28 (because Feb has no 30th), and from then on the renewal date drifts **backwards** by 1–2 days per month. For a 2-month subscription started on the 30th, the second renewal candidate (March 28) ends up *earlier* than the endDate (March 29), so the gating condition "nextDebitDate < endDate" evaluates to true and a third invoice is generated.

### The Bug

The defective pattern lives in two places:

- **subscription.service.ts** lines 203–225: setNextDebitDate() — called by InvoiceService.generateInvoices() after each successful invoice creation.
- **paygate.fuctions.ts** lines 774–806: updateNextDebitDate() — called from PayGate token-payment handling.

Both compute the next debit date as Timestamp.fromDate(moment(currentNextDebitDate).add(1, "M").toDate()), then gate writing it back with: if (nextDebitDate < subscription.endDate) → store, else → null.

### moment.js semantics that triggered it

moment(date).add(1, "M") "remembers no original day-of-month". When the target month is shorter, the day is **clamped to the last day of the target month**, and that clamped value becomes the new anchor for subsequent additions:

| Step | Anchor | +1 month | Why |
|------|--------|---------|-----|
| 1 | **Jan 30** | **Feb 28** (2025 non-leap) | Feb has no 30th → clamp to last day |
| 2 | **Feb 28** | **March 28** | moment forgets it was originally the 30th; just adds calendar month |
| 3 | March 28 | April 28 | drift continues |

### Why exactly three invoices, not two and not infinite

The subscription's endDate is set on creation as moment(startDate).add(period, "M").subtract(1, "d") — see parking-page-classic.component.ts:497, 605.

For Marcel:
- startDate = **2025-01-30**
- period = **2** months
- endDate = moment(Jan 30) + 2M − 1d = March 30 − 1d = **2025-03-29**

Trace through setNextDebitDate:

| Cycle | Before | +1M | nextDebitDate < endDate? | New nextDebitDate | Invoice generated 7d before? |
|------:|--------|-----|--------------------------|-------------------|------------------------------|
| 0 (creation) | Jan 30 | — | — | Jan 30 | Invoice #7638 due Jan 30 ✓ |
| 1 (after Jan 30 charge) | Jan 30 | **Feb 28** | Feb 28 < Mar 29 → true | Feb 28 | Invoice #7787 generated Feb 21, due Feb 28 ✓ |
| 2 (after Feb 28 charge) | Feb 28 | **March 28** | March 28 < March 29 → true | **March 28** | **Invoice #8045 generated March 21, due March 28 ✗** |
| 3 (after March 28 charge) | March 28 | April 28 | April 28 < March 29 → false | **null** | None — bug self-terminates |

This is **exactly** the pattern Marcel reported (Jan 30, Feb 28, March 28 settlements). It also answers the open question "why only 3 payments and not infinite?" — the third +1M jumps cleanly past endDate and zeros out the renewal anchor.

---

## Scope of the bug — who else is at risk

Any **fixed-term, monthly** subscription whose startDate falls on a day that **does not exist in at least one month it crosses** is vulnerable. Concretely:

| Start day | Always vulnerable? | Months that clip |
|-----------|--------------------|------------------|
| 31st | Yes | Feb (28/29), Apr, Jun, Sep, Nov |
| 30th | Yes (any sub crossing Feb) | Feb |
| 29th | Yes if crossing non-leap Feb | Feb (non-leap) |
| 28th | No — 28 always exists | none |
| 1st–27th | **Not vulnerable** | none |

The bug requires the renewal sequence to actually pass through a short month. So:
- A 2-month sub starting Jan 30 / Mar 30 / May 30 / Aug 30 / Oct 30 / Dec 30 → crosses Feb / Apr / Jun / Sep / Nov / Feb → **at risk**.
- A 2-month sub starting Feb 28 → renewals land on Mar 28, Apr 28 → not at risk because anchor never lost a day.

Recommended audit query: pull every fixed-term MONTHLY subscription with startDate.getDate() in {29, 30, 31} and paymentsMade > period, then cross-check against PayGate settlements.

---

## Why the existing safeguards didn't catch it

1. **shouldGenerateInvoice()** in subscription.service.ts:83 does try to handle Feb 29 → Feb 28 leap-year edge cases. But it is **defined and never called** anywhere in the codebase. The actual cron path (generateInvoices() → getSubscriptionsToBeInvoiced()) is purely a "nextDebitDate <= now+7d" query, so the date logic is entirely delegated to setNextDebitDate().
2. **Cron cronGenerateInvoices** runs daily at 06:00 SAST and creates an invoice for every active subscription whose nextDebitDate is within 7 days. There's no paymentsMade < period guard — the only stop condition is nextDebitDate = null, which setNextDebitDate only sets when the (drifted) candidate falls past endDate.
3. **No unit tests** exist for setNextDebitDate against month-end / leap-year inputs.

---

## Open Questions (now answered)

| Question from Marcel meeting | Answer |
|------------------------------|--------|
| Why only 3 charges, not infinite? | After the 3rd, +1M jumps **past** endDate and nextDebitDate is set to null, halting the cron. |
| Does it only affect 30th/31st starts crossing Feb? | Mostly yes. Any start day >28 that crosses a month with fewer days. 29th is at risk only when crossing a non-leap Feb. |
| Why did "Invoice Due Date" show no more invoices were due? | Correct — by the time the bug was investigated, nextDebitDate had already been set to null after the 3rd payment. The UI was right; the prior 3rd invoice had already happened. |
| Exact code path for invoice 8045? | cronGenerateInvoices (06:00 daily) → InvoiceService.generateInvoices() → getSubscriptionsToBeInvoiced() returned Marcel's sub because her nextDebitDate = 2025-03-28 was ≤ now+7d on 2025-03-21 → createInvoiceFromSubscription() → setNextDebitDate() advanced to April 28 (past endDate) → set null. |

---

## Recommended Fixes

### Immediate (code) — high priority

Add a "paymentsMade < period" guard for fixed-term subs in **both** setNextDebitDate (subscription.service.ts) and updateNextDebitDate (paygate.fuctions.ts). Stops invoicing as soon as the contractual number of payments has been collected, regardless of date drift.

> Note: paymentsMade is currently incremented *after* the comparison in setNextDebitDate. The comparison should happen before the increment, against the count that has *already* been billed (creation + N renewals).

### Stronger fix — preserve original anchor day

Store the original startDate.getDate() (or a renewalDayOfMonth) on the subscription, and recompute nextDebitDate deterministically each cycle: target month = moment(startDate).add(paymentsMade, "M"); day = min(renewalDayOfMonth, daysInMonth(targetMonth)). This preserves the "30th" intent: Jan 30 → Feb 28 → **Mar 30** → Apr 30 → ...

### Test coverage

Add unit tests for setNextDebitDate:
- Start Jan 30, period 2 → exactly 2 payments, ends with nextDebitDate = null
- Start Jan 31, period 3 → exactly 3 payments
- Start Feb 28 (non-leap), period 12 → exactly 12 payments, no drift
- Start Feb 29 (leap), period 12 → exactly 12 payments
- M2M sub started Jan 30 → endDate keeps rolling correctly month over month

### Operational — find & refund all victims

1. Query historical subscriptions per the rule above; produce list with paymentsMade > period.
2. For each, list the surplus invoice(s) and the PayGate transactionIds.
3. Decide refund vs payout-withhold per merchant. (For Marcel: hold back R800 from Marcel's landlord's next payout if it's already been paid out; otherwise PayGate refund.)
4. Email + WhatsApp affected clients.

---

## Resolution Steps (this specific subscription)

- [x] Root cause identified (this document).
- [ ] Pull subscription doc + 3 invoices from Firestore production export, confirm match.
- [ ] Verify with PayGate dashboard which transactionIds settled.
- [ ] Check whether the merchant payout for #8045 has already been disbursed.
- [ ] If not paid out: withhold R800 from next payout.
- [ ] If paid out: initiate PayGate refund of R800 to Marcel; reconcile with merchant.
- [ ] Update invoice #8045 status to VOIDED + record refund metadata.
- [ ] Notify Marcel of resolution (email + WhatsApp).
- [ ] Ship code fix (guard + tests + ideally renewalDayOfMonth field).
- [ ] Re-run audit query, refund anyone else affected.

---

## Updates Log

| Date | Author | Update |
|------|--------|--------|
| 2026-05-07 | Audit agent | Root cause identified — moment +1M clamping + "nextDebitDate < endDate" gating. Both setNextDebitDate and updateNextDebitDate are vulnerable. Fix proposals drafted. |`,
            expanded: false,
        },
        {
            id: '2026-02-04-001',
            date: '2026-02-04',
            title: 'Active Subscription with Voided Payment',
            entityType: 'subscription',
            entityId: 'kuD4Iv2Nmn6ITYnsdUm8',
            status: 'resolved',
            severity: 'high',
            description:
                'Parker (Phemelo Khoetha) has an active subscription but received a cancellation email. Funds were released via Paygate void, but subscription remains active.',
            auditFile: 'readmes/audits/2026-02-04_SUBSCRIPTION_ACTIVE_WITH_VOID_kuD4Iv2Nmn6ITYnsdUm8.md',
            rootCause: 'Double settlement request: Merchant rejected subscription at 08:16 (void succeeded), but a second settlement request at 08:42 re-activated it. Also found a code bug in httpVoidRequest causing ERR_HTTP_HEADERS_SENT error.',
            resolution: 'Code fixes applied: (1) Fixed double response bug in httpVoidRequest, (2) Added status guard to httpSettlementRequest, (3) Added frontend guard to WhatsApp accept link, (4) Added frontend guard to merchant portal. Subscriptions can no longer be approved after being cancelled or rejected.',
            auditContent: `# Subscription Audit: Active Subscription with Voided Payment

**Audit Date:** 2026-02-04
**Subscription ID:** kuD4Iv2Nmn6ITYnsdUm8
**Client Name:** Phemelo Khoetha
**Client Email:** nkelekhoetha@gmail.com
**Payment ID:** HrQKtOi73so1Aanw2BpB
**Status:** ROOT CAUSE IDENTIFIED
**Severity:** High

---

## Issue Summary

A parker (Phemelo Khoetha) has an active subscription, but they received an email stating the subscription was cancelled. The payment was voided with Paygate (funds released), yet the subscription remains in "Active" status.

### Known Context

- The client attempted to create **3 subscriptions**:
  1. One was **cancelled by the client**
  2. One **timed out** (expired after 24 hours without merchant approval)
  3. This subscription (kuD4Iv2Nmn6ITYnsdUm8) was **activated** despite being rejected

---

## ROOT CAUSE IDENTIFIED

Based on GCP Cloud Logging analysis, **two separate issues** caused this problem:

### Issue 1: Double Settlement Request

The merchant portal allowed a **second settlement request** to be processed AFTER the subscription was already rejected and voided.

### Issue 2: Code Bug in httpVoidRequest

There is a bug in paygate-routes.functions.js at line 171 where the HTTP response is sent twice, causing an ERR_HTTP_HEADERS_SENT error. While the void succeeded with PayGate, this error may have caused inconsistent state handling.

---

## Complete Timeline (2026-02-03)

| Time (UTC) | Function | Event |
|------------|----------|-------|
| 07:51:59 | httpProcessTokenPayment | Client pays, subscription status → paymentInitiated |
| 07:52:01 | httpProcessTokenPayment | Subscription updated |
| 07:52:05 | httpProcessTokenPayment | Subscription updated |
| 07:54:03 | httpSettlementRequest | **FIRST settlement request** - "Request for Settlement Received" |
| 08:16:19 | httpVoidRequest | CORS preflight request (204) |
| 08:16:20 | httpVoidRequest | **Void request started** |
| 08:16:24 | httpVoidRequest | PayGate response: **"Reversal done"**, resultCode: 990026 (SUCCESS) |
| 08:16:24 | httpVoidRequest | Payment HrQKtOi73so1Aanw2BpB updated with voided=true |
| 08:16:25 | httpVoidRequest | **ERROR**: ERR_HTTP_HEADERS_SENT: Cannot set headers after they are sent |
| 08:16:27 | onPaymentUpdate | Email sent to client: **"Funds Released"** |
| 08:16:29 | onSubscriptionUpdate | Email sent to client: **"Your parking request was rejected"** |
| 08:16:34 | onSubscriptionUpdate | Email sent to merchant: **"Request rejected"** (thami.moyo@excellerate.co.za) |
| 08:42:23 | httpSettlementRequest | **SECOND settlement request** - subscription updated |
| 08:42:24 | httpSettlementRequest | **SECOND settlement request** - subscription set to ACTIVE |

---

## Key Evidence from Logs

### 1. Void Request Succeeded with PayGate

PayGateResponse:
- resultCode: '990026'
- reference: 'HrQKtOi73so1Aanw2BpB'
- transactionId: '1145821825'
- statusName: 'Completed'
- transactionStatusDescription: 'Reversed'
- resultDescription: 'Reversal done'
- amount: '150000' (R1,500.00)
- cardNumber: '528497xxxxxx2574'

### 2. Code Error After Void

Error in httpVoidRequest: Error [ERR_HTTP_HEADERS_SENT]: Cannot set headers after they are sent to the client
at /workspace/lib/paygate/paygate-routes.functions.js:171:17

### 3. Rejection Emails Sent

- Client received: "Your parking request was rejected"
- Client received: "Funds Released"
- Merchant received: "Request rejected"

### 4. Second Settlement 26 Minutes Later

At 08:42, a second httpSettlementRequest was called, which:
- Updated the subscription status to active
- Triggered the subscription approval flow

---

## Why This Happened

### Scenario Analysis

1. **07:54** - Merchant clicked "Accept" → First settlement request sent
2. **08:16** - Merchant clicked "Reject" → Void request sent, succeeded
3. **08:42** - Something triggered a second settlement request

**Most Likely Cause:** The first settlement request at 07:54 may have been queued or retried. When it finally processed at 08:42, it didn't check if the subscription was already rejected.

**Alternative:** The merchant may have had two browser tabs open, or clicked Accept on a cached page after rejecting.

---

## Code Issues Identified

### 1. httpVoidRequest Double Response Bug

**File:** parkupp-functions/functions/src/paygate/paygate-routes.functions.ts (compiled to line 171)

The function is sending an HTTP response twice, which throws the ERR_HTTP_HEADERS_SENT error.

### 2. Missing Status Check in Settlement

**File:** parkupp-functions/functions/src/paygate/paygate.fuctions.ts

The merchantApprovedSettlementRequest function should check if the subscription is still in paymentInitiated status before processing settlement. Currently, it only checks if the payment is already settled.

### 3. No Idempotency on Settlement

There's no mechanism to prevent duplicate settlement requests for the same subscription.

---

## Recommended Fixes

### Immediate Fix 1: Add Status Check to Settlement

In merchantApprovedSettlementRequest, add a check for subscription status before settling.

### Immediate Fix 2: Fix Double Response in httpVoidRequest

Review paygate-routes.functions.ts around line 171 to ensure only one response is sent.

### Long-term Fix 1: Add Idempotency Keys

Store a settlement request ID and check for duplicates before processing.

### Long-term Fix 2: Add Subscription Audit Log

Create a collection to track all status changes with timestamps and sources.

---

## Resolution Steps for This Subscription

1. [x] Root cause identified
2. [ ] Verify current subscription status in Firestore
3. [ ] Check if payment was actually settled with PayGate (despite void)
4. [ ] Determine if client should have access:
   - If payment was voided: Cancel subscription, apologize to client
   - If payment was actually taken: Client can keep access, but log the discrepancy
5. [ ] Notify affected parties of the resolution
6. [ ] Implement code fixes to prevent recurrence

---

## Updates Log

| Date | Update | By |
|------|--------|-----|
| 2026-02-04 | Initial audit document created | System |
| 2026-02-04 | ROOT CAUSE IDENTIFIED via GCP Cloud Logging analysis | System |

---

## Related Documents

- Subscription Model: parkupp-model/src/models/subscription.model.ts
- Listener Functions: parkupp-functions/functions/src/listeners/listeners.functions.ts
- PayGate Functions: parkupp-functions/functions/src/paygate/paygate.fuctions.ts
- PayGate Routes: parkupp-functions/functions/src/paygate/paygate-routes.functions.ts
- Subscription Service: parkupp-functions/functions/src/services/subscription.service.ts`,
            expanded: false,
        },
    ]

    filteredIssues: KnownIssue[] = []
    statusFilter: string = 'all'
    severityFilter: string = 'all'
    entityTypeFilter: string = 'all'


    ngOnInit() {
        this.applyFilters()
    }

    applyFilters() {
        this.filteredIssues = this.knownIssues.filter((issue) => {
            if (this.statusFilter !== 'all' && issue.status !== this.statusFilter) {
                return false
            }
            if (this.severityFilter !== 'all' && issue.severity !== this.severityFilter) {
                return false
            }
            if (this.entityTypeFilter !== 'all' && issue.entityType !== this.entityTypeFilter) {
                return false
            }
            return true
        })
    }

    onStatusFilterChange(event: Event) {
        this.statusFilter = (event.target as HTMLSelectElement).value
        this.applyFilters()
    }

    onSeverityFilterChange(event: Event) {
        this.severityFilter = (event.target as HTMLSelectElement).value
        this.applyFilters()
    }

    onEntityTypeFilterChange(event: Event) {
        this.entityTypeFilter = (event.target as HTMLSelectElement).value
        this.applyFilters()
    }

    getStatusBadgeClass(status: string): string {
        switch (status) {
            case 'investigating':
                return 'bg-warning text-dark'
            case 'resolved':
                return 'bg-success'
            case 'monitoring':
                return 'bg-info'
            case 'wont-fix':
                return 'bg-secondary'
            default:
                return 'bg-secondary'
        }
    }

    getSeverityBadgeClass(severity: string): string {
        switch (severity) {
            case 'critical':
                return 'bg-danger'
            case 'high':
                return 'bg-warning text-dark'
            case 'medium':
                return 'bg-info'
            case 'low':
                return 'bg-secondary'
            default:
                return 'bg-secondary'
        }
    }

    getEntityTypeIcon(entityType: string): string {
        switch (entityType) {
            case 'subscription':
                return 'fa-solid fa-file-contract'
            case 'payment':
                return 'fa-solid fa-credit-card'
            case 'merchant':
                return 'fa-solid fa-store'
            case 'client':
                return 'fa-solid fa-user'
            default:
                return 'fa-solid fa-question'
        }
    }

    getEntityLink(issue: KnownIssue): string | null {
        switch (issue.entityType) {
            case 'subscription':
                return `/admin/subscription/${issue.entityId}`
            case 'payment':
                return `/admin/payment/${issue.entityId}`
            default:
                return null
        }
    }

    getGithubAuditLink(auditFile: string): string {
        // This assumes your repo is accessible - adjust the base URL as needed
        return `https://github.com/your-org/parkupp-firebase/blob/main/${auditFile}`
    }

    copyToClipboard(text: string) {
        navigator.clipboard.writeText(text)
    }

    toggleExpand(issue: KnownIssue) {
        issue.expanded = !issue.expanded
    }

    copyAuditContent(issue: KnownIssue) {
        // Get the rendered HTML from the DOM
        const auditElement = document.querySelector('.audit-rendered')
        if (auditElement) {
            const htmlContent = auditElement.innerHTML
            
            // Create a blob with HTML content for rich text copying
            const blob = new Blob([htmlContent], { type: 'text/html' })
            const plainTextBlob = new Blob([issue.auditContent], { type: 'text/plain' })
            
            // Use ClipboardItem to copy both HTML and plain text
            const clipboardItem = new ClipboardItem({
                'text/html': blob,
                'text/plain': plainTextBlob
            })
            
            navigator.clipboard.write([clipboardItem]).then(() => {
                alert('Formatted audit content copied! You can paste it into Word.')
            }).catch(() => {
                // Fallback to plain text if HTML copy fails
                navigator.clipboard.writeText(issue.auditContent).then(() => {
                    alert('Audit content copied as plain text.')
                })
            })
        } else {
            // Fallback if element not found
            navigator.clipboard.writeText(issue.auditContent).then(() => {
                alert('Audit content copied as plain text.')
            })
        }
    }

    /**
     * Convert markdown to HTML for display
     */
    renderMarkdown(markdown: string): SafeHtml {
        let html = this.escapeHtml(markdown)

        // Convert code blocks first (before other processing)
        html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
            return `<pre class="code-block"><code>${code.trim()}</code></pre>`
        })

        // Convert headers
        html = html.replace(/^######\s+(.*)$/gm, '<h6>$1</h6>')
        html = html.replace(/^#####\s+(.*)$/gm, '<h5>$1</h5>')
        html = html.replace(/^####\s+(.*)$/gm, '<h4>$1</h4>')
        html = html.replace(/^###\s+(.*)$/gm, '<h3>$1</h3>')
        html = html.replace(/^##\s+(.*)$/gm, '<h2>$1</h2>')
        html = html.replace(/^#\s+(.*)$/gm, '<h1>$1</h1>')

        // Convert horizontal rules
        html = html.replace(/^---+$/gm, '<hr>')

        // Convert bold
        html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

        // Convert inline code (but not in code blocks)
        html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')

        // Convert checkboxes
        html = html.replace(/^(\s*)- \[ \]\s+(.*)$/gm, '$1<div class="checkbox unchecked"><i class="fa-regular fa-square"></i> $2</div>')
        html = html.replace(/^(\s*)- \[x\]\s+(.*)$/gm, '$1<div class="checkbox checked"><i class="fa-solid fa-square-check"></i> $2</div>')

        // Convert unordered lists
        html = html.replace(/^(\s*)- (.*)$/gm, '$1<li>$2</li>')
        
        // Convert ordered lists
        html = html.replace(/^(\s*)\d+\.\s+(.*)$/gm, '$1<li class="ordered">$2</li>')

        // Convert tables
        html = this.convertTables(html)

        // Convert line breaks to paragraphs (but preserve structure)
        html = html.replace(/\n\n/g, '</p><p>')
        html = '<p>' + html + '</p>'

        // Clean up empty paragraphs and fix nested elements
        html = html.replace(/<p>\s*<\/p>/g, '')
        html = html.replace(/<p>\s*(<h[1-6]>)/g, '$1')
        html = html.replace(/(<\/h[1-6]>)\s*<\/p>/g, '$1')
        html = html.replace(/<p>\s*(<hr>)/g, '$1')
        html = html.replace(/(<hr>)\s*<\/p>/g, '$1')
        html = html.replace(/<p>\s*(<pre)/g, '$1')
        html = html.replace(/(<\/pre>)\s*<\/p>/g, '$1')
        html = html.replace(/<p>\s*(<div class="checkbox)/g, '$1')
        html = html.replace(/(<\/div>)\s*<\/p>/g, '$1')
        html = html.replace(/<p>\s*(<li>)/g, '<ul>$1')
        html = html.replace(/(<\/li>)\s*<\/p>/g, '$1</ul>')
        html = html.replace(/<p>\s*(<table)/g, '$1')
        html = html.replace(/(<\/table>)\s*<\/p>/g, '$1')

        return this.sanitizer.bypassSecurityTrustHtml(html)
    }

    private escapeHtml(text: string): string {
        const map: { [key: string]: string } = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
        }
        return text.replace(/[&<>]/g, (m) => map[m])
    }

    private convertTables(html: string): string {
        const lines = html.split('\n')
        let inTable = false
        let tableHtml = ''
        const result: string[] = []

        for (const line of lines) {
            if (line.includes('|') && line.trim().startsWith('|')) {
                if (!inTable) {
                    inTable = true
                    tableHtml = '<table class="table table-sm table-bordered">'
                }

                // Skip separator lines
                if (line.match(/^\|[\s\-:|]+\|$/)) {
                    continue
                }

                const cells = line.split('|').filter((c) => c.trim() !== '')
                const isHeader = !tableHtml.includes('<tbody>')

                if (isHeader && !tableHtml.includes('<thead>')) {
                    tableHtml += '<thead><tr>'
                    cells.forEach((cell) => {
                        tableHtml += `<th>${cell.trim()}</th>`
                    })
                    tableHtml += '</tr></thead><tbody>'
                } else {
                    tableHtml += '<tr>'
                    cells.forEach((cell) => {
                        tableHtml += `<td>${cell.trim()}</td>`
                    })
                    tableHtml += '</tr>'
                }
            } else {
                if (inTable) {
                    tableHtml += '</tbody></table>'
                    result.push(tableHtml)
                    tableHtml = ''
                    inTable = false
                }
                result.push(line)
            }
        }

        if (inTable) {
            tableHtml += '</tbody></table>'
            result.push(tableHtml)
        }

        return result.join('\n')
    }
}
